import { Info, Target } from "lucide-react";
import type { IntentContract } from "@/services/testing";

/**
 * The planner's structured reading of the ask, shown next to the plan so the user can confirm the
 * agent understood BEFORE approving.
 *
 *  - `oracle`      → "What this test verifies" chips: the checkable outcomes the run must prove.
 *  - `intentGaps`  → parts of the ask the plan does NOT verify.
 *
 * Advisory only: gaps never block approval, and the panel renders nothing when there is nothing to
 * say. (Note the naming split — the wire event calls this `intent_gaps`, the stored plan body calls
 * the same data `contract_gaps`.)
 */
export function IntentPanel({
	contract,
	gaps,
}: {
	contract?: IntentContract;
	gaps?: string[];
}) {
	const oracles = contract?.oracle ?? [];
	const hasGaps = Array.isArray(gaps) && gaps.length > 0;
	if (!hasGaps && oracles.length === 0) return null;

	return (
		<div className="mb-2 space-y-1.5">
			{oracles.length > 0 && (
				<div className="rounded-md border border-border/70 bg-muted/20 px-2 py-1.5 text-[11px]">
					<div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
						<Target className="h-3 w-3" />
						What this test verifies
					</div>
					<div className="flex flex-wrap gap-1">
						{oracles.map((o, i) => (
							<span
								key={`${o.kind}-${o.target ?? i}`}
								title={o.expectation ?? undefined}
								className="max-w-full truncate rounded-full border border-border bg-card px-2 py-0.5 font-mono text-[11px] text-foreground/80"
							>
								{o.kind}
								{o.target ? ` on ${o.target}` : ""}
							</span>
						))}
					</div>
				</div>
			)}

			{hasGaps && (
				<div className="rounded-md border border-warning/40 bg-warning/10 px-2 py-1.5 text-[11px] text-warning-strong">
					<div className="mb-1 flex items-center gap-1 font-semibold text-warning-strong">
						<Info className="h-3 w-3 shrink-0" />
						This plan may not verify everything you asked
					</div>
					<ul className="ml-4 list-disc space-y-0.5 leading-snug opacity-90">
						{gaps!.map((gap, i) => (
							<li key={i}>{gap}</li>
						))}
					</ul>
					<div className="mt-1 text-[11px] opacity-70">
						You can still approve — or describe the missing part in chat to extend the plan.
					</div>
				</div>
			)}
		</div>
	);
}
